<template>
  <MonitorWrapper class="gd624-monitor">
    <StatusBar v-bind="statusBarConfig" />

    <el-tabs type="card" v-model="activeName">
      <el-tab-pane :label="$t('数据卡片')" name="data">
        <template v-if="activeName === 'data'">
          <div
            v-for="(sensor, index) in sensors"
            :class="['gd624-monitor-card', `state-${sensor.alarmState >= 1 ? 1 : 0}`]"
            :key="index"
          >
            <div class="gd624-monitor-card_content">
              <span>{{ sensor.name || '----' }}</span>
              <span class="value">{{ sensor.value }}</span>
              <span>{{ sensor.unitName }}</span>
            </div>
          </div>
        </template>
      </el-tab-pane>
      <el-tab-pane
        :label="$t('曲线图')"
        name="graph"
        :style="{ '--col-num': colNum, '--row-num': rowNum }"
      >
        <template v-if="activeName === 'graph'">
          <Chart v-for="item in sensors" :config="item" :key="item.name" />
        </template>
      </el-tab-pane>
    </el-tabs>
  </MonitorWrapper>
</template>

<script>
import StatusBar from './components/status-bar.vue';
import Chart from './components/chart.vue';
import monitorMixin from '@/mixins/monitorMixin';
import Mock from 'mockjs';

export default {
  name: 'GD624',
  mixins: [monitorMixin],
  components: { StatusBar, Chart },
  data() {
    return {
      activeName: 'data',
      statusBarConfig: {
        model: {
          temp: '',
          humidity: '',
          windSpeed: '',
          windDirection: '',
          pm25: '',
          pm10: ''
        },
        options: [
          {
            icon: 'icon-temperature',
            label: '温度',
            prop: 'temp',
            unit: '℃'
          },
          {
            icon: 'icon-humidity2',
            label: '湿度',
            prop: 'humidity',
            unit: '%'
          },
          {
            icon: 'icon-wind-speed',
            label: '风速',
            prop: 'windSpeed',
            unit: 'm/s'
          },
          {
            icon: 'icon-wind-direction',
            label: '风向',
            prop: 'windDirection',
            unit: '',
            format: degrees => {
              degrees = degrees % 360;
              if (degrees < 0) degrees += 360;

              let direction = '';

              // 定义方向区间和对应的中文名称
              if (degrees >= 337.5 || degrees < 22.5) {
                direction = '北';
              } else if (degrees >= 22.5 && degrees < 67.5) {
                direction = '东北';
              } else if (degrees >= 67.5 && degrees < 112.5) {
                direction = '东';
              } else if (degrees >= 112.5 && degrees < 157.5) {
                direction = '东南';
              } else if (degrees >= 157.5 && degrees < 202.5) {
                direction = '南';
              } else if (degrees >= 202.5 && degrees < 247.5) {
                direction = '西南';
              } else if (degrees >= 247.5 && degrees < 292.5) {
                direction = '西';
              } else if (degrees >= 292.5 && degrees < 337.5) {
                direction = '西北';
              }

              return this.$t(direction); // 默认返回北
            }
          },
          {
            icon: 'icon-pm-2_5',
            label: 'PM2.5',
            prop: 'pm25',
            unit: 'µg/m³'
          },
          {
            icon: 'icon-PM10',
            label: 'PM10',
            prop: 'pm10',
            unit: 'µg/m³'
          }
        ]
      },
      slots: {},
      sensorMap: {},
      enableMock: false
    };
  },
  computed: {
    sensors() {
      return Object.values(this.sensorMap);
    },
    colNum() {
      const len = this.sensors.length;

      const colNum = len < 4 ? len : len % 3 === 0 ? 3 : len % 2 === 0 ? 2 : 3;

      return colNum;
    },
    rowNum() {
      const len = this.sensors.length;

      return Math.ceil(len / this.colNum);
    }
  },
  methods: {
    updateData(data) {
      const { mods, beginTime } = data;
      const { gd624Mod } = mods;

      const { temp, humidity, windSpeed, windDirection, pm25, pm10 } = gd624Mod;

      Object.assign(this.statusBarConfig.model, {
        temp,
        humidity,
        windSpeed,
        windDirection,
        pm25,
        pm10
      });

      const { sensorDataList } = gd624Mod;

      sensorDataList.forEach(e => {
        if (this.sensorMap[e.name]) {
          const sensor = this.sensorMap[e.name];
          if (sensor.data.length > 20) sensor.data.shift();

          sensor.data.push([beginTime, e.value]);
          sensor.value = e.value;
        } else {
          this.$set(this.sensorMap, e.name, { ...e, data: [[beginTime, e.value]] });
        }
      });

      // this.$set(this.slots, gd624Mod.slot, [...sensorDataList]);
    },
    startMock() {
      if (!this.enableMock) return;

      setInterval(() => {
        this.updateData(
          Mock.mock({
            mods: {
              gd624Mod: {
                temp: '@integer(10, 40, 2)',
                humidity: '@integer(30, 90, 2)',
                windSpeed: '@integer(0, 20, 2)',
                windDirection: '@integer(0, 360, 2)',
                pm25: '@integer(0, 100, 2)',
                pm10: '@integer(0, 150, 2)',
                sensorDataList: [
                  {
                    name: 'CO',
                    value: '@integer(0, 100, 2)',
                    unitName: 'ppm',
                    alarmState: '0'
                  },
                  {
                    name: 'CO₂',
                    value: '@integer(300, 5000, 2)',
                    unitName: 'ppm',
                    alarmState: '@integer(0, 1)'
                  },
                  {
                    name: 'SO₂',
                    value: '@integer(0, 20, 2)',
                    unitName: 'ppm',
                    alarmState: '0'
                  },
                  {
                    name: 'H₂S',
                    value: '@integer(0, 10, 2)',
                    unitName: 'ppm',
                    alarmState: '0'
                  },
                  {
                    name: 'Cl₂',
                    value: '@integer(0, 5, 2)',
                    unitName: 'ppm',
                    alarmState: '0'
                  },
                  {
                    name: 'HCl',
                    value: '@integer(0, 10, 2)',
                    unitName: 'ppm',
                    alarmState: '0'
                  },
                  {
                    name: 'HCN',
                    value: '@integer(0, 5, 2)',
                    unitName: 'ppm',
                    alarmState: '0'
                  },
                  {
                    name: 'NO₂',
                    value: '@integer(0, 5, 2)',
                    unitName: 'ppm',
                    alarmState: '0'
                  },
                  {
                    name: 'CH₄',
                    value: '@integer(0, 100, 2)',
                    unitName: 'ppm',
                    alarmState: '0'
                  },
                  {
                    name: 'PH₃',
                    value: '@integer(0, 5, 2)',
                    unitName: 'ppm',
                    alarmState: '0'
                  },
                  {
                    name: 'H₂',
                    value: '@integer(0, 4, 2)',
                    unitName: 'ppm',
                    alarmState: '0'
                  },
                  {
                    name: 'O₂',
                    value: '@integer(18, 22, 2)',
                    unitName: '%',
                    alarmState: '0'
                  }
                ]
              }
            },
            beginTime: +new Date()
          })
        );

        this.hideLoading();
        this.hideTimeoutNotice();
      }, 1000);
    }
  },
  mounted() {
    this.startMock();
  }
};
</script>

<style lang="scss">
.gd624-monitor {
  --gap: 10px;
  --col-num: 4;
  --row-num: 4;

  &-card {
    width: calc(100% / var(--col-num) - var(--gap) * (var(--col-num) - 1) / var(--col-num));
    height: min(160px, calc(25% - var(--gap) * (var(--col-num) - 1) / var(--col-num)));

    border-radius: 10px;
    border: 1px solid #3c4c73;
    box-sizing: border-box;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-around;

    position: relative;

    &::before {
      content: ' ';

      position: absolute;
      left: 50%;
      top: 0;
      transform: translateX(-50%);
      display: inline-block;
      width: 10%;
      height: 3px;
      background: var(--color);
    }

    &_content {
      position: absolute;
      left: 50%;
      top: 10px;
      transform: translateX(-50%);
      width: calc(100% - 20px);
      height: calc(100% - 20px);
      background: linear-gradient(to right bottom, #3d4d76, #262c3c);
      border-radius: 6px;

      display: flex;
      flex-direction: column;
      justify-content: space-around;
      align-items: center;
    }

    &.state-0 {
      --color: #03b36e;
    }

    &.state-1 {
      --color: #ff4847;
    }

    .value {
      color: var(--color);
      font-size: 28px;
      text-align: center;
    }
  }

  .el-tabs {
    height: calc(100% - 74px);

    // &__nav{

    // }

    .el-tab-pane {
      height: 100%;

      display: flex;
      flex-wrap: wrap;
      align-content: flex-start;
      gap: var(--gap);
      padding: 10px;

      box-sizing: border-box;
      overflow-y: auto;
      overflow-x: hidden;

      @include scrollar-style-thin;
    }

    &__header {
      border-bottom: unset !important;

      margin: 0;
    }

    &__content {
      border: 1px solid #1b3450;
      height: calc(100% - 41px);
      box-sizing: border-box;
    }
  }
}
</style>
